import React, { useEffect, useMemo, useState } from 'react';
import AppLayout from '../../../../components/layout/AppLayout';
import WYSIWYG from '../../../../components/editor/WISIWYG';
import { message, Button, Select, Space } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { TLesson } from '../../../../models/courses/lesson';
import { useCourseApi } from '../../../../api/courses/courses';

const LessonSlide = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { getLessonDetailsById, saveLessonDetails } = useCourseApi();

    const lesson: TLesson = location.state?.lesson;
    const [editingLesson, setEditingLesson] = useState<TLesson | undefined>(lesson);
    const [loading, setIsLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);

    const [getContent, setGetContent] = useState<() => { html: string; css: string }>(() => ({ html: '', css: '' }));
    const [selectedLanguage, setSelectedLanguage] = useState<'fr' | 'ar' | 'en'>('fr');

    const sanitizeSlide = (lessonData: TLesson): NonNullable<TLesson['slide']> => {
        if (!lessonData.slide) {
            return {
                id: 0,
                lesson_id: lessonData.id,
                html: '',
                css: '',
                html_ar: '',
                css_ar: '',
                html_en: '',
                css_en: '',
            };
        }

        return {
            id: lessonData.slide.id ?? 0,
            lesson_id: lessonData.id,
            html: lessonData.slide.html ?? '',
            css: lessonData.slide.css ?? '',
            html_ar: lessonData.slide.html_ar ?? '',
            css_ar: lessonData.slide.css_ar ?? '',
            html_en: lessonData.slide.html_en ?? '',
            css_en: lessonData.slide.css_en ?? '',
        };
    };

    const mergeContentForLanguage = (
        slide: NonNullable<TLesson['slide']>,
        language: 'fr' | 'ar' | 'en',
        content: { html: string; css: string }
    ) => {
        switch (language) {
            case 'ar':
                return {
                    ...slide,
                    html_ar: content.html,
                    css_ar: content.css,
                };
            case 'en':
                return {
                    ...slide,
                    html_en: content.html,
                    css_en: content.css,
                };
            default:
                return {
                    ...slide,
                    html: content.html,
                    css: content.css,
                };
        }
    };

    useEffect(() => {
        setIsLoading(true);

        if (lesson) {
            getLessonDetailsById(lesson.id)
                .then((results) => {
                    const lessonData = results;
                    setEditingLesson({
                        ...lessonData.data,
                        slide: sanitizeSlide(lessonData.data),
                    });
                })
                .catch((err) => {
                    message.error(err.response.data ?? err.message);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
        // eslint-disable-next-line
    }, [lesson]);

    const save = async (): Promise<void> => {
        if (!editingLesson || saving) {
            return;
        }

        const content = getContent(); // Récupérer le contenu HTML et CSS
        const currentSlide = sanitizeSlide(editingLesson);

        const data: TLesson = {
            ...editingLesson,
            slide: mergeContentForLanguage(currentSlide, selectedLanguage, content),
        };

        try {
            setSaving(true);
            const response = await saveLessonDetails(data);
            setEditingLesson({
                ...response.data,
                slide: sanitizeSlide(response.data),
            });
            message.success('Leçon enregistrée avec succès.');
        } catch (err: any) {
            message.error(err.response?.data ?? err.message ?? err);
        } finally {
            setSaving(false);
        }
    };

    const handleLanguageChange = (value: 'fr' | 'ar' | 'en') => {
        if (editingLesson) {
            const content = getContent();
            const updatedSlide = mergeContentForLanguage(sanitizeSlide(editingLesson), selectedLanguage, content);
            setEditingLesson({
                ...editingLesson,
                slide: updatedSlide,
            });
        }
        setSelectedLanguage(value);
    };

    const selectedContent = useMemo(() => {
        const sourceLesson = editingLesson ?? lesson;
        if (!sourceLesson) {
            return { html: '', css: '' };
        }

        const slide = sanitizeSlide(sourceLesson);
        switch (selectedLanguage) {
            case 'ar':
                return { html: slide?.html_ar ?? '', css: slide?.css_ar ?? '' };
            case 'en':
                return { html: slide?.html_en ?? '', css: slide?.css_en ?? '' };
            default:
                return { html: slide?.html ?? '', css: slide?.css ?? '' };
        }
    }, [editingLesson, lesson, selectedLanguage]);

    const handleBackNavigation = () => {
        navigate(-1);
    };

    return (
        <AppLayout
            title={lesson.name}
            loading={loading}
            onBackNavigation={handleBackNavigation}
            showBackButton={true}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Select
                    value={selectedLanguage}
                    onChange={handleLanguageChange}
                    options={[
                        { value: 'fr', label: 'Français' },
                        { value: 'en', label: 'English' },
                        { value: 'ar', label: 'العربية' },
                    ]}
                    style={{ width: 200 }}
                />
                <WYSIWYG setGetContent={setGetContent} initialContent={selectedContent} />
            </Space>
            <Button
                type="default"
                shape="circle"
                icon={<SaveOutlined />}
                onClick={save}
                loading={saving}
                disabled={saving}
                style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    width: '56px',
                    height: '56px',
                    zIndex: 1000,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    color: '#1890ff'
                }}
            />
        </AppLayout>
    );
};

export default LessonSlide;
